import { ComponentType } from '@angular/cdk/portal';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdditionalAction, AdditionalColumn } from './cases-table/cases-table.component';
import { PayeeCasesService } from './payee-cases.service';
import { CaseTableItem, ClientOption, Pagination, Stage } from './types';

export const UNKNOWN_CLIENT_NAME = 'Unknown Client';
export const UNKNOWN_STATE_LABEL = 'Unknown State';

@Component({
  selector: 'payee-cases',
  templateUrl: './payee-cases.component.html',
  styleUrls: ['./payee-cases.component.scss'],
})
export class PayeeCasesComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy = new Subject<void>();

  @Input() allowEdit = true;
  @Input() allowDelete = true;

  @Input() stages: Stage[] = [
    'draft',
    'ongoing',
    'closed',
  ];

  @Input() additionalFilters: ComponentType<any>[] = [];
  @Input() additionalActions: AdditionalAction[] = [];
  @Input() additionalColumns: AdditionalColumn[] = [];
  @Input() showInfo = true;

  @Input() set getCasesPath(path: string | null) {
    this.payeeCasesService.getCasesPath.next(path);
  }

  @Input() set getDebtorsPath(path: string | null) {
    this.payeeCasesService.getDebtorsPath.next(path);
  }

  stateOptions: string[] = [];

  get draftSelected(): boolean { return this.payeeCasesService.stage.value === 'draft'; }
  get ongoingSelected(): boolean { return this.payeeCasesService.stage.value === 'ongoing'; }
  get closedSelected(): boolean { return this.payeeCasesService.stage.value === 'closed'; }

  readonly dataSource = new MatTableDataSource<CaseTableItem>();

  get loading() { return this.payeeCasesService.loading; }
  pagination?: Pagination;

  constructor(
    private router: Router,
    private payeeCasesService: PayeeCasesService,
  ) { }

  async ngOnInit() {
    this.payeeCasesService.resetFilters();

    this.payeeCasesService.cases
      .pipe(takeUntil(this.destroy))
      .subscribe(cases => {
        this.pagination = cases.pagination;
        this.dataSource.data = cases.data;
      });

    this.payeeCasesService.onInit();

    this.payeeCasesService.filters
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setFilterQueryParams());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const selectedClient = this.payeeCasesService.client.value;

    if (changes.clientOptions && selectedClient?.name === UNKNOWN_CLIENT_NAME && selectedClient.id) {
      const clientOptions = changes.clientOptions.currentValue as ClientOption[];
      const client = clientOptions.find(c => c.id === selectedClient?.id);
      if (client) {
        this.payeeCasesService.client.next(client);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.payeeCasesService.onDestroy();
  }

  pageChange(event: PageEvent): void {
    this.payeeCasesService.setPagination(event);
  }

  private setFilterQueryParams(): void {
    this.router.navigate([], {
      queryParams: this.payeeCasesService.getFilters(),
      queryParamsHandling: 'merge',
    });
  }
}
